/**
 * TaskCard component - displays a task in the Kanban board
 */

import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { clsx } from 'clsx';
import { Building2, Calendar, CheckSquare, Clock, Flag, ListChecks, Mail, Mic, Paperclip } from 'lucide-react';
import { isPast, isToday } from 'date-fns';
import { format } from '@/i18n/dateFns';
import { Link } from 'react-router-dom';
import Avatar from '@components/ui/Avatar';
import Badge from '@components/ui/Badge';
import type { Task } from '@services/api';

interface TaskCardProps {
  task: Task;
  onClick?: () => void;
  showLabels?: boolean;
  showAssignee?: boolean;
  showDueDate?: boolean;
  showPriority?: boolean;
  showSubtaskProgress?: boolean;
  showAttachments?: boolean;
  showSourceMeeting?: boolean;
  compact?: boolean;
}

const priorityColors: Record<string, string> = {
  urgent: 'border-l-red-400',
  high: 'border-l-orange-400',
  medium: 'border-l-yellow-400',
  low: 'border-l-blue-400',
  none: '',
};

// Priority labels for display (exported for potential use in other components)
export const priorityLabels: Record<string, string> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  none: '',
};

// Text color for the priority flag icon (non-color-only signal is provided via sr-only text)
const priorityFlagColors: Record<string, string> = {
  urgent: 'text-red-500',
  high: 'text-orange-500',
  medium: 'text-yellow-500',
  low: 'text-blue-500',
  none: '',
};

export default function TaskCard({
  task,
  onClick,
  showLabels = true,
  showAssignee = true,
  showDueDate = true,
  showPriority = true,
  showSubtaskProgress = true,
  showAttachments = true,
  showSourceMeeting = true,
  compact = false,
}: TaskCardProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'manipulation',
  };

  // Track whether a drag just occurred so the synthetic click that fires after
  // pointerup (including cancelled/micro drags that snap back) does not open the modal.
  const wasDraggingRef = useRef(false);
  useEffect(() => {
    if (isDragging) {
      wasDraggingRef.current = true;
      return;
    }
    // Suppress the drop's click only, never a later intentional activation.
    const timer = window.setTimeout(() => { wasDraggingRef.current = false; }, 0);
    return () => window.clearTimeout(timer);
  }, [isDragging]);

  const handleActivate = () => {
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return;
    }
    onClick?.();
  };

  const isOverdue = task.dueDate && !task.isCompleted && isPast(new Date(task.dueDate));
  const isDueToday = task.dueDate && isToday(new Date(task.dueDate));

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleActivate}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' && !isDragging) {
          event.preventDefault();
          onClick?.();
        } else {
          listeners?.onKeyDown?.(event);
        }
      }}
      aria-label={`${task.taskKey}: ${task.title}`}
      className={clsx(
        'group cursor-pointer rounded-xl border border-neutral-200 border-l-[3px] bg-white p-3.5 shadow-sm',
        'transition-[border-color,box-shadow] duration-150 hover:border-neutral-300 hover:shadow-md',
        'dark:border-neutral-700 dark:bg-neutral-900',
        showPriority && (priorityColors[task.priority] || ''),
        isDragging && 'opacity-50 shadow-lg',
        task.isCompleted && 'opacity-60'
      )}
    >
      {/* Labels */}
      {showLabels && task.labels && task.labels.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {task.labels.slice(0, 3).map((label) => (
            <Badge key={label.id} color={label.color} size="sm">
              {label.name}
            </Badge>
          ))}
          {task.labels.length > 3 && (
            <Badge size="sm" variant="subtle">
              +{task.labels.length - 3}
            </Badge>
          )}
        </div>
      )}

      {/* Task key and title */}
      <div className="flex items-start gap-2">
        {task.isCompleted && (
          <CheckSquare className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
              {task.taskKey}
            </span>
            {showPriority && task.priority && task.priority !== 'none' && (
              <span
                className={clsx('inline-flex items-center', priorityFlagColors[task.priority] || '')}
              >
                <Flag className="h-3 w-3" fill="currentColor" aria-hidden="true" />
                <span className="sr-only">
                  {t(`task.priorities.${task.priority}`)}
                </span>
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleActivate();
            }}
            className={clsx(
              'block w-full break-words text-left text-sm font-semibold leading-snug text-neutral-900 dark:text-white',
              'rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              task.isCompleted && 'line-through'
            )}
          >
            {task.title}
          </button>
        </div>
      </div>

      {/* Description preview (non-compact only) */}
      {!compact && task.description && (
        <p className="mt-1.5 line-clamp-2 text-xs text-neutral-600 dark:text-neutral-400">
          {task.description}
        </p>
      )}

      {/* Source badges */}
      {(task.sourceMeetingId || task.sourceEmailId) && (
        <div className="mt-2 flex items-center gap-2">
          {task.sourceMeetingId && (
            <Link
              to={`/meetings/${task.sourceMeetingId}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              <Mic className="w-3 h-3" />
              <span>{t('nav.meetings')}</span>
            </Link>
          )}
          {task.sourceEmailId && (
            <Link
              to={`/emails/${task.sourceEmailId}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 hover:underline"
            >
              <Mail className="w-3 h-3" />
              <span>{t('nav.emails')}</span>
            </Link>
          )}
          {task.sourceLanguage && task.sourceLanguage !== 'en' && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400 uppercase font-medium">
              {task.sourceLanguage}
            </span>
          )}
        </div>
      )}

      {/* Footer with metadata */}
      {(Boolean(showDueDate && task.dueDate) || Boolean(task.estimatedMinutes) || Boolean(showSubtaskProgress && task.subtaskCount) || Boolean(showAttachments && task.attachmentCount) || Boolean(showAssignee && task.assignee) || Boolean(showSourceMeeting && task.sourceMeeting)) && <div className="mt-3 flex items-center justify-between gap-2 border-t border-neutral-100 pt-2.5 dark:border-neutral-800">
        <div className="flex items-center gap-2">
          {/* Due date */}
          {showDueDate && task.dueDate && (
            <div
              className={clsx(
                'flex items-center gap-1 text-xs',
                isOverdue && 'text-red-500',
                isDueToday && !isOverdue && 'text-orange-500',
                !isOverdue && !isDueToday && 'text-neutral-500 dark:text-neutral-400'
              )}
            >
              <Calendar className="h-3 w-3" />
              <span>{format(new Date(task.dueDate), 'MMM d')}</span>
              {isOverdue && <span className="sr-only">(overdue)</span>}
              {isDueToday && !isOverdue && <span className="sr-only">(due today)</span>}
            </div>
          )}

          {/* Time estimate/logged */}
          {task.estimatedMinutes && task.estimatedMinutes > 0 && (
            <div className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
              <Clock className="h-3 w-3" />
              <span>
                {task.actualMinutes > 0 && `${formatMinutes(task.actualMinutes)}/`}
                {formatMinutes(task.estimatedMinutes)}
              </span>
            </div>
          )}

          {/* Subtask progress */}
          {showSubtaskProgress && task.subtaskCount !== undefined && task.subtaskCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
              <ListChecks className="h-3 w-3" />
              <span>
                {task.subtaskCompletedCount || 0}/{task.subtaskCount}
              </span>
            </div>
          )}

          {/* Attachments */}
          {showAttachments && task.attachmentCount !== undefined && task.attachmentCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
              <Paperclip className="h-3 w-3" />
              <span>{task.attachmentCount}</span>
            </div>
          )}

          {/* Source meeting indicator (legacy - shown when sourceMeetingId is absent but sourceMeeting object exists) */}
          {showSourceMeeting && task.sourceMeeting && !task.sourceMeetingId && (
            <div
              className="flex items-center gap-1 text-xs text-primary-500 dark:text-primary-400"
              title={`From meeting: ${task.sourceMeeting.title}`}
            >
              <Mic className="h-3 w-3" />
            </div>
          )}
        </div>

        {/* Assignee */}
        {showAssignee && task.assignee && (
          <div className="flex min-w-0 items-center gap-1.5">
            {/* On touch/small screens the title tooltip never shows, so surface the name inline */}
            {!compact && (
              <span className="truncate text-xs text-neutral-500 dark:text-neutral-400 sm:hidden">
                {task.assignee.displayName}
                {task.assignee.assigneeType === 'contact' && task.assignee.company
                  ? ` (${task.assignee.company})`
                  : ''}
              </span>
            )}
            <div
              className="relative flex-shrink-0"
              title={
                task.assignee.assigneeType === 'contact'
                  ? `${task.assignee.displayName}${task.assignee.company ? ` (${task.assignee.company})` : ''}`
                  : task.assignee.displayName
              }
            >
              <Avatar
                size="xs"
                name={task.assignee.displayName}
                {...(task.assignee.avatarUrl ? { src: task.assignee.avatarUrl } : {})}
              />
              {/* Badge indicator for contact assignees */}
              {task.assignee.assigneeType === 'contact' && (
                <div className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white dark:bg-neutral-800">
                  <Building2 className="h-2.5 w-2.5 text-neutral-500 dark:text-neutral-400" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>}
    </div>
  );
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}
